/**
 * Bilingual field access.
 *
 * The API returns paired columns — `name_de` / `name_en`, `how_de` / `how_en` —
 * and the UI has to pick one at runtime. Doing that with a template-literal
 * index (`type[`name_${lang}`]`) forced every model interface to carry an
 * `[key: string]: any` escape hatch, which disabled type checking on exactly
 * the access most likely to be wrong.
 *
 * `localized()` does the same job with the index signatures removed: the field
 * name is checked against the object's own keys, and German is the fallback
 * because it is the authoritative language of the municipal source data.
 */

import { Lang } from '../types';

/** Field names on `T` that exist as a `_de` / `_en` pair. */
export type BilingualKey<T> = Extract<
    { [K in keyof T]: K extends `${infer Base}_de`
        ? `${Base}_en` extends keyof T ? Base : never
        : never }[keyof T],
    string
>;

/**
 * Read a bilingual field in the requested language, falling back to German.
 *
 *   localized(wasteType, 'name', 'en')  →  wasteType.name_en ?? wasteType.name_de
 */
export function localized<T extends object, K extends BilingualKey<T>>(
    obj: T | null | undefined,
    field: K,
    lang: Lang
): string {
    if (!obj) return '';
    const record = obj as Record<string, unknown>;
    const value = record[`${field}_${lang}`] ?? record[`${field}_de`];
    return typeof value === 'string' ? value : '';
}

/** Narrow an i18next language tag ("de-CH", "en-GB") to a supported language. */
export function toLang(language: string | undefined): Lang {
    return language?.toLowerCase().startsWith('en') ? 'en' : 'de';
}
